import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Dropdown, Menu, Checkbox, Row, Col, Input, Button } from "antd"
import { DownOutlined, SearchOutlined } from "@ant-design/icons"
import Spinner from "components/common/Spinner"
import styles from "./index.module.css"
import { useDispatch, useSelector } from "react-redux"
import { updateQuestionnaireTemplateExperience, getQuestionnaireTemplates } from "store/question"
import { toaster } from "components/common/Toast"
import { useTranslation } from "react-i18next"
const TripSelector = ({ uuid, trips, allTrips, onSelect, isLoading = false, isDisabled = false, isOpen, onOpenChange }) => {
  const dispatch = useDispatch()
  const [searchText, setSearchText] = useState("")
  const [selectedTripIds, setSelectedTripIds] = useState(trips?.map((trip) => trip.id) || [])
  const [internalLoading, setInternalLoading] = useState(false)
  const { t } = useTranslation()
 const {loading}=useSelector((state)=>state.question.questionnaireTemplates)
  useEffect(() => {
    setSelectedTripIds(trips?.map((trip) => trip.id) || [])
  }, [trips])

  useEffect(() => {
    if (!isLoading) {
      setInternalLoading(false)
    }
  }, [isLoading])

  const handleVisibleChange = (visible) => {
    // Only toggle when clicking the trigger button
    const isClickingTrigger = document.activeElement?.closest(".ant-dropdown-trigger")
    if (isClickingTrigger) {
      setSelectedTripIds(trips?.map((trip) => trip.id) || [])

      onOpenChange?.(!isOpen)
    } else if (visible) {
      onOpenChange?.(true)
      setSelectedTripIds(trips?.map((trip) => trip.id) || [])
    }
  }

  const handleTripSelect = async (tripId, checked) => {
    const newSelectedTripIds = checked ? [...selectedTripIds, tripId] : selectedTripIds.filter((id) => id !== tripId)

    setSelectedTripIds(newSelectedTripIds)
    setInternalLoading(true)
    try {
      await onSelect(newSelectedTripIds)
    } finally {
      if (!isLoading) {
        setInternalLoading(false)
      }
    }
  }

  const selectedTrips = allTrips?.filter((item) => selectedTripIds.includes(item.id)) || []
  const hasMultipleTrips = selectedTrips.length > 1
  const additionalTripsCount = hasMultipleTrips ? ` +${selectedTrips.length - 1} more` : ""
  let mainTitle = selectedTrips[0]?.title || ""
  let truncatedTitle = mainTitle.length > 10 ? mainTitle.slice(0, 10) + "..." : mainTitle
  const displayText = selectedTrips.length > 0 ? `${truncatedTitle}${additionalTripsCount}` : "Select Experience"

  const handleUpdate = () => {
    const payload = {
      questionnaire_template: {
        trip_ids: selectedTripIds
      }
    }
    dispatch(updateQuestionnaireTemplateExperience(uuid, payload)).then((res) => {
      toaster.success(res.message)
      onOpenChange?.(false)
      dispatch(getQuestionnaireTemplates(1, 10))
    })
  }

  const filteredTrips = allTrips?.filter((trip) => trip.title.toLowerCase().includes(searchText.toLowerCase())) || []

  const allSelected = filteredTrips.length > 0 && filteredTrips.every((trip) => selectedTripIds.includes(trip.id))
  const someSelected = filteredTrips.some((trip) => selectedTripIds.includes(trip.id))

  const handleSelectAll = (checked) => {
    const filteredTripIds = filteredTrips.map((trip) => trip.id)
    let newSelectedTripIds

    if (checked) {
      // Add all filtered trip IDs that aren't already selected
      newSelectedTripIds = [...new Set([...selectedTripIds, ...filteredTripIds])]
    } else {
      // Remove all filtered trip IDs that are selected
      newSelectedTripIds = selectedTripIds.filter((id) => !filteredTripIds.includes(id))
    }

    setSelectedTripIds(newSelectedTripIds)
    onSelect(newSelectedTripIds)
  }

  const renderMenuContent = () => {
    if (internalLoading) {
      return (
        <div className={styles.loadingContainer}>
          <Spinner size="small" />
        </div>
      )
    }

    if (!allTrips?.length) {
      return (
        <Menu.Item key="no-experience" className={styles.noExperienceItem}>
          {t("settings.no_experiences_found")}
        </Menu.Item>
      )
    }

    return (
      <>
        <div className={styles.searchContainer}>
          <Input
            prefix={<SearchOutlined className={styles.searchIcon} />}
            placeholder="Search experiences..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className={styles.searchInput}
          />
        </div>
        {filteredTrips.length > 0 && (
          <div className={styles.selectAllContainer}>
            <Checkbox
              checked={allSelected}
              indeterminate={!allSelected && someSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
              disabled={isLoading}
            >
              {t("settings.select_all")}
            </Checkbox>
          </div>
        )}
        {filteredTrips.length > 0 ? (
          <div className={styles.tripList}>
            {filteredTrips.map((trip) => (
              <Menu.Item key={trip.id}>
                <Checkbox
                  checked={selectedTripIds.includes(trip.id)}
                  onChange={(e) => handleTripSelect(trip.id, e.target.checked)}
                  disabled={isLoading}
                >
                  {trip.title}
                </Checkbox>
              </Menu.Item>
            ))}
          </div>
        ) : (
          <Menu.Item key="no-results" className={styles.noExperienceItem}>
            {t("settings.no_experiences_found")}
          </Menu.Item>
        )}
      </>
    )
  }

  const dropdownContent = (
    <div className={styles.dropdownWrapper}>
      <Menu className={styles.menuContainer}>{renderMenuContent()}</Menu>
      <div className={styles.dropdownFooter}>
        <Button
          color="primary"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation()
            onOpenChange?.(false)
            setSelectedTripIds(trips?.map((trip) => trip.id) || [])
          }}
          className={styles.cancelButton}
        >
          {t("global.cancel")}
        </Button>
        <Button
          color="primary"
          variant="solid"
          onClick={(e) => {
            e.stopPropagation()
            handleUpdate()
          }}
          loading={loading}
          disabled={!filteredTrips.length || !allTrips.length || loading}
        >
          {t("global.update")}
        </Button>
      </div>
    </div>
  )

  return (
    <div className={styles.dropdownContentBox}>
      <Dropdown
        overlay={dropdownContent}
        trigger={["click"]}
        visible={isOpen}
        onVisibleChange={handleVisibleChange}
        disabled={isDisabled}
        overlayStyle={{ pointerEvents: "auto" }}
        getPopupContainer={() => document.body}
      >
        <button
          type="button"
          className={`${styles.dropdownTrigger} ${isDisabled ? styles.disabled : ""}`}
          disabled={isDisabled}
          aria-disabled={isDisabled}
          onClick={(e) => {
            if (isDisabled) {
              e.preventDefault()
              e.stopPropagation()
            }
          }}
          onKeyDown={(e) => {
            if (isDisabled) return
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              onOpenChange?.(!isOpen)
            }
          }}
        >
          <Row align="middle" justify="space-between" className={styles.triggerContent}>
            <Col flex="auto">
              <span>{displayText}</span>
            </Col>
            <Col>
              <DownOutlined className={styles.dropdownIcon} />
            </Col>
          </Row>
        </button>
      </Dropdown>
    </div>
  )
}

TripSelector.propTypes = {
  uuid: PropTypes.string.isRequired,
  trips: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired
    })
  ).isRequired,
  allTrips: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired
    })
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isOpen: PropTypes.bool,
  onOpenChange: PropTypes.func
}

export default TripSelector
